import React from "react";
import { Box, Portal } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { useGovTheme } from "./useGovTheme";

/**
 * A short, light confetti burst for REAL milestones only — your vote is recorded, a group goes live,
 * setup is complete. Never for routine saves (that would cheapen it). Pointer-transparent, gone in about
 * a second, and skipped entirely for "reduce motion".
 */
export function useCelebrate() {
  const t = useGovTheme();
  const reduce = useReducedMotion();
  const [burst, setBurst] = React.useState(0);
  const celebrate = React.useCallback(() => { if (!reduce && process.env.NODE_ENV !== "test") setBurst((n) => n + 1); }, [reduce]);
  const colours = [t.primary, t.secondary, t.success, t.warning, t.purple];
  const pieces = React.useMemo(() => Array.from({ length: 28 }, (_, i) => ({
    x: (Math.random() - 0.5) * 520, y: -(120 + Math.random() * 260), r: (Math.random() - 0.5) * 540,
    d: 0.9 + Math.random() * 0.5, c: colours[i % colours.length], w: 6 + Math.random() * 6,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [burst]);
  // Clear the burst when the SLOWEST piece lands, so no piece is cut off mid-flight.
  const slowest = pieces.reduce((m, p, i) => (p.d > pieces[m].d ? i : m), 0);
  const node = (
    // Portal: a card with a blur (backdrop-filter) or a transform traps `position: fixed`, so rendered in
    // place the layer followed the card and pieces flying sideways widened the page at 390px. On
    // document.body it is truly fixed, and overflow hidden keeps every piece inside the screen.
    <Portal>
      <Box position="fixed" inset={0} pointerEvents="none" zIndex={2000} overflow="hidden" aria-hidden>
        {/* No AnimatePresence: every piece fades itself out, and TypeScript 4.9 rejects its JSX type. */}
        {burst > 0 && (
          <Box key={burst} position="absolute" left="50%" top="55%">
            {pieces.map((p, i) => (
              <motion.div key={i} style={{ position: "absolute", width: p.w, height: p.w * 0.45, borderRadius: 2, background: p.c }}
                initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
                animate={{ x: p.x, y: [0, p.y, p.y + 420], rotate: p.r, opacity: [1, 1, 0] }}
                transition={{ duration: p.d + 0.6, ease: "easeOut", times: [0, 0.45, 1] }}
                onAnimationComplete={i === slowest ? () => setTimeout(() => setBurst(0), 50) : undefined} />
            ))}
          </Box>
        )}
      </Box>
    </Portal>
  );
  return { celebrate, node };
}
